import { getNameAndSurname, prefix } from "src/utils/users.utils";
import { fakeUserInfoHandle, strapiUrl, strapiCheckUrl } from "./constants";
import axios from "axios";
import { isStrapi } from "src/api/config/systemSwitch";
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { isPWA } from "src/utils/mobile";
import { resolveResourceUrl } from "src/utils/url.utils";

type User = {
  Id: number;
  Email?: string;
  AccountName?: string;
  [key: string]: any;
};

const TARGET_DOMAINS = ["in4partners.com", "partnerabie.com", "partnervmeste.ru"];

const isValidEmail = (email?: string): email is string => {
  return !!email && /^(\d+)@pertnervmeste\.ru$/.test(email);
};

const replaceDomain = (email: string, newDomain: string) => {
  const [localPart] = email.split("@");
  return `${localPart}@${newDomain}`;
};

const replaceAccountName = (accountName: string, newEmail: string) => {
  const parts = accountName.split("|");
  parts[parts.length - 1] = newEmail;
  return parts.join("|");
};

const processUsers = (users: User[]): User[] => {
  const result: User[] = [...users];

  const existingEmails = new Set(
    users.map((u) => u.Email).filter(Boolean)
  );

  users.forEach((user) => {
    if (!isValidEmail(user.Email)) return;

    TARGET_DOMAINS.forEach((domain) => {
      const newEmail = replaceDomain(user.Email!, domain);

      // защита от дублей
      if (existingEmails.has(newEmail)) return;

      const clonedUser: User = {
        ...user,
        Email: newEmail,
        AccountName: user.AccountName
          ? replaceAccountName(user.AccountName, newEmail)
          : user.AccountName,
      };

      result.push(clonedUser);
      existingEmails.add(newEmail);
    });
  });

  return result;
};

export const getAiHealthRequest = async () => {
  const aiEnabled = await axios.get(`${strapiUrl}ai/health`);
  return aiEnabled.data.data.enabled
}

export const getUserRequest = async (emails: string[]) => {
  if (isStrapi) {
    let token: string | null = null;

    if (!isPWA()) {
      const result = await SecureStoragePlugin.get({ key: 'accessToken' });
      token = result.value;
    }

    const headers = {
      Accept: "application/json;odata=nometadata",
      "Content-Type": "application/json;odata=nometadata",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const updatedEmails = emails.map(email => {
      return email.replace(
        /^(\d+)@(in4partners\.com|partnerabie\.com)$/,
        "$1@pertnervmeste.ru"
      );
    });

    const customRequest = await axios.post(
      `${strapiUrl}custom-users/by-emails`,
      {
        emails: updatedEmails,
        fields: [
          "id",
          "username",
          "email",
          "PersonalNumber",
          "City",
          "Name",
          "Position",
          "Location",
          "Department",
          "Department_RUS",
          "blocked"
        ],
      },
      {
        headers,
        withCredentials: true,
      }
    );
    const responses = await Promise.all([customRequest]);
    // Собираем результаты
    const response = responses.flatMap((response) => response.data.data);
    let tempUsers: any = [];
    response.forEach((user: any) => {
      tempUsers = [
        ...tempUsers,
        {
          Id: user.id,
          DisplayName: user.Name,
          AccountName: prefix + user.email,
          DirectReports:
            (user.Reports &&
              user.Reports.length > 0 &&
              user.Reports.filter((report: any) => !report.blocked).map((report: any) => prefix + report.email)) ||
            [],
          Email: user.email,
          PictureUrl:
            (user.PictureUrl && resolveResourceUrl(user.PictureUrl.url, strapiCheckUrl)) || null,
          Roles: user.Roles,
          UserProfileProperties: [
            {
              Key: "PreferredNameRus",
              Value: user.Name,
            },
            {
              Key: "abiGlobalID",
              Value: user.PersonalNumber,
            },
            {
              Key: "UserName",
              Value: user.PersonalNumber,
            },
            {
              Key: "abiPersonnelNumber",
              Value: user.PersonalNumber,
            },
            {
              Key: "EmployeeNumber",
              Value: user.PersonalNumber,
            },
            {
              Key: "Manager",
              Value:
                (user.ManagerDev && prefix + user.ManagerDev.email) || null,
            },
            {
              Key: "TitleRUS",
              Value: user.Position,
            },
            {
              Key: "DepartmentRUS",
              Value: user.Department_RUS || user.Department,
            },
            {
              Key: "SPS-JobTitle",
              Value: user.Position,
            },
            {
              Key: "PreferredName",
              Value: user.Name,
            },
          ],
        },
      ];
    });
    tempUsers = processUsers(tempUsers);
    let users: any = [];
    emails.forEach((email) => {
      if (
        tempUsers.filter(
          (user: any) => user.Email === email && user.DisplayName
        ).length > 0
      ) {
        users = [
          ...users,
          ...tempUsers.filter(
            (user: any) => user.Email === email && user.DisplayName
          ),
        ];
      } else {
        users = [...users, fakeUserInfoHandle(email.toLowerCase())];
      }
    });
    return users;
  }
};

export async function getUserProperties<T extends string>(
  email: string,
  object: T[]
): Promise<Record<T, any> | null> {
  if (!object.length) return null;

  let token: string | null = null;

  if (!isPWA()) {
    const result = await SecureStoragePlugin.get({ key: 'accessToken' });
    token = result.value;
  }

  const headers = {
    Accept: "application/json;odata=nometadata",
    "Content-Type": "application/json;odata=nometadata",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await axios.get(`${strapiUrl}users`, {
    params: {
      filters: { email: { $eq: email } },
      fields: [
        "id",
        "username",
        "email",
        "PersonalNumber",
        "City",
        "Name",
        "Position",
        "Location",
        "Department",
        "Department_RUS",
      ],
      populate: {
        ManagerDev: {
          // Получаем менеджера со всеми полями
          populate: "*",
        },
        Reports: {
          // Подчиненные со всеми полями
          populate: "*",
        },
        Roles: {
          // Роли со всеми полями
          populate: "*",
        },
        PictureUrl: {
          // Получаем аватару со всеми полями
          populate: "*",
        },
      },
    },
    headers,
    withCredentials: true,
  });

  let returnData: any = [];
  response.data.forEach((user: any) => {
    const NameAndSurname = getNameAndSurname(user?.Name, user?.email);

    const [FirstName, LastName] = NameAndSurname.split(" ");

    returnData = [
      ...returnData,
      {
        FirstName: FirstName || null,
        LastName: LastName || null,
        AccountName: prefix + user.email,
        apiGlobalID: user?.PersonalNumber,
        abiGlobalID: user?.PersonalNumber,
        EmployeeNumber: user?.PersonalNumber,
        PictureUrl:
          (user?.PictureUrl && resolveResourceUrl(user.PictureUrl.url, strapiCheckUrl)) || null,
        PictureURL:
          (user?.PictureUrl && resolveResourceUrl(user.PictureUrl.url, strapiCheckUrl)) || null,
        UserName: user.PersonalNumber,
        PreferredName: user?.Name,
        PreferredNameRUS: user?.Name,
        TitleRUS: user?.Position,
        JobTitleRus: user?.Position,
        JobTitle: user?.Position,
        "SPS-Location": user?.Location,
        Region: user?.Location,
        DepartmentRUS: user?.Department_RUS || user?.Department,
        Department: user?.Department_RUS || user?.Department,
        BaseOfficeLocation: user?.Location,
        ManagerFullNameRUS:
          (user?.ManagerDev && prefix + user?.ManagerDev.Name) || null,
        WorkEmail: user?.email,
      },
    ];
  });
  let returnObj: any = {};
  returnData.forEach((item: any) => {
    Object.keys(item).forEach((key) => {
      returnObj[key] = item[key];
    });
  });
  return returnObj;
}
